import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  TuiButtonModule,
  TuiDataListModule,
  TuiDialogContext,
  TuiDialogService,
  TuiDropdownModule,
  TuiHostedDropdownModule,
  TuiLoaderModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import {
  TuiDataListDropdownManagerModule,
  TuiFileLike,
  TuiInputModule,
  TuiInputNumberModule,
} from '@taiga-ui/kit';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  POLYMORPHEUS_CONTEXT,
  PolymorpheusComponent,
} from '@tinkoff/ng-polymorpheus';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@shared/services/alert.service';
import { InvoiceService } from '@shared/services/invoice.service';
import { FileInputComponent } from '@shared/components/forms/file-input/file-input.component';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { Store } from '@ngxs/store';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { CURRENCIES } from '@shared/constants/currency';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { PackageTypeFormComponent } from '@shared/components/catalogs/package-type-form/package-type-form.component';
import { PackageTypeService } from '@shared/services/package-type.service';
import { fromFormArrayValue } from '@shared/utils/observables';
import {
  InvoiceDto,
  InvoiceItemDto,
  InvoiceStatisticsItem,
} from '../../../../types/invoice';
import { TransformToFormControl } from '../../../../types/common';
import { PackageType } from '../../../../types/package-type';

export type InvoiceParseDialogInputData = {
  invoice?: InvoiceDto;
  confirmText?: string;
  openFile?: () => unknown;
};

type DialogPhase = 'upload' | 'parsing' | 'review' | 'edit' | 'saving';

type InvoiceItemForm = TransformToFormControl<InvoiceItemDto>;
type InvoiceStatisticsForm = TransformToFormControl<InvoiceStatisticsItem>;

type InvoiceForm = TransformToFormControl<
  Omit<InvoiceDto, 'items' | 'statisticsItems'>
> & {
  items: FormArray<FormGroup<InvoiceItemForm>>;
  statisticsItems: FormArray<FormGroup<InvoiceStatisticsForm>>;
};

@Component({
  selector: 'app-invoice-parse-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TuiButtonModule,
    TuiDataListModule,
    TuiDataListDropdownManagerModule,
    TuiDropdownModule,
    TuiInputModule,
    TuiInputNumberModule,
    TuiLoaderModule,
    TuiTextfieldControllerModule,
    FileInputComponent,
    InputDateComponent,
    AutocompleteComponent,
    TuiHostedDropdownModule,
  ],
  templateUrl: './invoice-parse-dialog.component.html',
  styleUrl: './invoice-parse-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceParseDialogComponent implements OnInit {
  phase$ = new BehaviorSubject<DialogPhase>('upload');
  packages$ = new BehaviorSubject<PackageType[]>([]);
  openPackageTypeDialog$ = new Subject<Nullable<string>>();

  title$ = this.phase$.pipe(
    map(phase => {
      const isExistingInvoiceEdit =
        phase === 'edit' ||
        (phase === 'saving' && !!this.context.data?.invoice);

      return isExistingInvoiceEdit
        ? 'Редактирование инвойса'
        : 'Проверьте данные инвойса';
    }),
  );

  confirmText$ = new BehaviorSubject('Сохранить');

  fillSeller$ = new Subject<Nullable<string>>();
  fillBuyer$ = new Subject<Nullable<string>>();

  upload$ = new Subject<void>();
  save$ = new Subject<void>();

  fileControl = new FormControl<TuiFileLike | null>(null, Validators.required);

  invoiceForm = this.fb.group<InvoiceForm>({
    orderId: this.fb.control(null),
    buyerId: this.fb.control(null),
    currencyLetter: this.fb.control(null),
    sellerId: this.fb.control(null),
    id: this.fb.control(null),
    number: this.fb.control(null),
    date: this.fb.control(null),
    buyer: this.fb.control(null),
    seller: this.fb.control(null),
    cost: this.fb.control(null),
    previewId: this.fb.control(null),
    items: this.fb.array<FormGroup<InvoiceItemForm>>([]),
    statisticsItems: this.fb.array<FormGroup<InvoiceStatisticsForm>>([]),
    groupId: this.fb.control(null),
  });

  computed = this.fb.group({
    netWeight: this.fb.control({ disabled: true, value: 0 }),
  });

  farms$ = this.cargoCatalogsService.farms$;

  intermediaries$ = this.cargoCatalogsService.sellers$;

  readonly parsingMessage =
    'Идет парсинг инвойса через ИИ, пожалуйста подождите...';

  protected readonly CURRENCIES = CURRENCIES;
  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<
      InvoiceDto | void,
      InvoiceParseDialogInputData
    >,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly alert: AlertService,
    private readonly invoiceService: InvoiceService,
    private readonly store: Store,
    private readonly cargoCatalogsService: CargoCatalogsService,
    private readonly dialogs: TuiDialogService,
    private readonly packageTypeService: PackageTypeService,
  ) {}

  onUpload() {
    this.upload$
      .pipe(
        filter(() => !!this.fileControl.value),
        tap(() => this.phase$.next('parsing')),
        switchMap(() =>
          this.invoiceService
            .extractFromPdf(this.fileControl.value as File)
            .pipe(
              catchError(() => {
                this.alert.showError('Не удалось распарсить инвойс');
                this.phase$.next('upload');
                return of(null);
              }),
            ),
        ),
        filter((invoice): invoice is InvoiceDto => invoice !== null),
        tap(invoice => {
          this.populateForm(invoice);
          this.phase$.next('review');
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSave() {
    this.save$
      .pipe(
        filter(() => this.invoiceForm.valid),
        switchMap(() => {
          const formPhase = this.phase$.value;
          this.phase$.next('saving');
          const invoice = this.invoiceForm.getRawValue();

          if (this.invoiceForm.dirty) {
            return this.invoiceService.saveInvoice(invoice).pipe(
              tap(() => {
                this.alert.showSuccess('Инвойс сохранен');
                this.closeDialog(invoice);
              }),
              catchError(() => {
                this.alert.showError('Не удалось сохранить инвойс');
                this.phase$.next(formPhase === 'edit' ? 'edit' : 'review');
                return of(null);
              }),
            );
          }
          return of(null).pipe(
            tap(() => {
              this.closeDialog(invoice);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onFillSellerId() {
    return this.fillSeller$
      .pipe(
        filter(name => name !== null && name !== ''),
        withLatestFrom(this.farms$),
        tap(([name, farms]) => {
          const farmId = farms.find(s => s.name === name)?.id ?? null;
          this.invoiceForm.controls.sellerId.setValue(farmId);
          this.invoiceForm.controls.sellerId.markAsTouched();
          this.invoiceForm.controls.sellerId.markAsDirty();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onFillBuyerId() {
    return this.fillBuyer$
      .pipe(
        filter(name => name !== null && name !== ''),
        withLatestFrom(this.intermediaries$),
        tap(([name, intermediaryCompany]) => {
          const intermediaryCompanyId =
            intermediaryCompany.find(s => s.name === name)?.id ?? null;
          this.invoiceForm.controls.buyerId.setValue(intermediaryCompanyId);
          this.invoiceForm.controls.buyerId.markAsTouched();
          this.invoiceForm.controls.buyerId.markAsDirty();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenPackageTypeDialog() {
    this.openPackageTypeDialog$
      .pipe(
        switchMap(packageTypeCode => {
          const packageTypeData = packageTypeCode
            ? ({
                id: null,
                code: packageTypeCode,
                name: packageTypeCode,
                size: null,
                cargoPlaceQuantityJson: [],
                weight: null,
                tags: [],
              } as unknown as PackageType)
            : null;
          return this.dialogs.open(
            new PolymorpheusComponent(PackageTypeFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data: { packageType: packageTypeData },
            },
          );
        }),
        tap(() => this.loadPackages()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  get items() {
    return this.invoiceForm.controls.items;
  }

  get statisticsItems() {
    return this.invoiceForm.controls.statisticsItems;
  }

  fetchCatalogs() {
    this.cargoCatalogsService.farms$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.cargoCatalogsService.buyers$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.cargoCatalogsService.fetchBuyers$.next();
    this.cargoCatalogsService.fetchFarms$.next();
  }

  calcTotalNetWeight() {
    return fromFormArrayValue<FormArray<FormGroup<InvoiceItemForm>>>(
      this.invoiceForm.controls.items,
    )
      .pipe(
        tap(items => {
          const totalNetWeight = items.reduce((p, c) => {
            return p + (c.netWeight ?? 0);
          }, 0);
          this.computed.controls.netWeight.setValue(totalNetWeight);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.onUpload();
    this.onSave();
    this.fetchCatalogs();
    this.onFillSellerId();
    this.onFillBuyerId();
    this.calcTotalNetWeight();
    this.onOpenPackageTypeDialog();
    this.loadPackages();

    const invoice = this.context.data?.invoice;
    const confirmText = this.context.data?.confirmText;
    if (invoice) {
      this.populateForm(invoice);
      this.phase$.next('edit');
    }
    if (confirmText) {
      this.confirmText$.next(confirmText);
    }
  }

  populateForm(invoice: InvoiceDto) {
    const { items, statisticsItems, ...rest } = invoice;
    this.invoiceForm.patchValue(rest);

    this.invoiceForm.controls.items.reset();

    items.forEach((item, index, list) => {
      const form = this.createItemGroup(item);
      this.invoiceForm.controls.items.push(form, {
        emitEvent: index === list.length - 1,
      });
    });

    this.invoiceForm.setControl(
      'statisticsItems',
      this.fb.array(
        (statisticsItems ?? []).map(stat => this.createStatisticsGroup(stat)),
      ),
    );
  }

  createItemGroup(item: Partial<InvoiceItemDto> = {}) {
    return this.fb.group<InvoiceItemForm>({
      netWeight: this.fb.control(item.netWeight ?? null),
      ruName: this.fb.control(item.ruName ?? null),
      name: this.fb.control(item.name ?? null),
      quantity: this.fb.control(item.quantity ?? null),
      price: this.fb.control(item.price ?? null),
      cost: this.fb.control(item.cost ?? null),
      size: this.fb.control(item.size ?? null),
      height: this.fb.control(item.height ?? null),
      packageType: this.fb.control(item.packageType ?? null),
    });
  }

  createStatisticsGroup(stat: Partial<InvoiceStatisticsItem> = {}) {
    return this.fb.group<InvoiceStatisticsForm>({
      name: this.fb.control(stat.name ?? null),
      ruName: this.fb.control(stat.ruName ?? null),
      packageQuantity: this.fb.control(stat.packageQuantity ?? null),
      quantity: this.fb.control(stat.quantity ?? null),
      tnVed: this.fb.control(stat.tnVed ?? null),
      grossWeight: this.fb.control(stat.grossWeight ?? null),
      netWeight: this.fb.control(stat.netWeight ?? null),
      cost: this.fb.control(stat.cost ?? null),
    });
  }

  addItem() {
    this.items.push(this.createItemGroup());
  }

  removeItem(index: number) {
    this.items.removeAt(index);
  }

  addStatisticsItem() {
    this.statisticsItems.push(this.createStatisticsGroup());
  }

  removeStatisticsItem(index: number) {
    this.statisticsItems.removeAt(index);
  }

  loadPackages() {
    this.packageTypeService
      .getAll()
      .pipe(
        tap(packages => this.packages$.next(packages)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  recalcItemWeight(item: FormGroup<InvoiceItemForm>, showAlert = false) {
    const packages = this.packages$.value;
    const packageTypeCode = item.controls.packageType.value;
    const quantity = item.controls.quantity.value;

    if (!packageTypeCode) {
      return;
    }

    const pkg = packages.find(
      p =>
        p.code === packageTypeCode || (p.tags ?? []).includes(packageTypeCode),
    );

    if (showAlert) {
      if (!pkg) {
        this.alert.showWarning('Нет упаковки данного типа');
      }

      if (pkg && !pkg?.weight) {
        this.alert.showWarning('Вес упаковки отсутствует');
      }
    }

    if (pkg && pkg.weight !== null && quantity !== null) {
      item.controls.netWeight.setValue((pkg.weight ?? 0) * (quantity ?? 0));
      item.controls.netWeight.markAsTouched();
      item.controls.netWeight.markAsDirty();
    }
  }

  recalcAllItemWeights() {
    this.items.controls.forEach(item => this.recalcItemWeight(item));
  }

  openFile() {
    return this.context.data.openFile;
  }

  closeDialog(invoice?: InvoiceDto) {
    this.context.$implicit.next(invoice);
    this.context.$implicit.complete();
  }
}
